const videoBackgroundTextPlaybackRequests = new WeakMap();

const playVideoBackgroundTextVideo = (video) => {
  if (!video) return Promise.resolve(false);

  const pendingRequest = videoBackgroundTextPlaybackRequests.get(video);
  if (pendingRequest) return pendingRequest;

  const playRequest = video.play();
  if (!playRequest || typeof playRequest.then !== 'function') {
    return Promise.resolve(true);
  }

  const handledRequest = playRequest
    .then(() => true)
    .catch((error) => {
      if (error?.name !== 'AbortError') {
        console.warn('Video playback failed:', error);
      }

      return false;
    })
    .finally(() => {
      if (videoBackgroundTextPlaybackRequests.get(video) === handledRequest) {
        videoBackgroundTextPlaybackRequests.delete(video);
      }
    });

  videoBackgroundTextPlaybackRequests.set(video, handledRequest);
  return handledRequest;
};

const loadVideoBackgroundTextSources = (video) => {
  if (!video || video.dataset.videoLoaded === 'true') return;

  const sources = video.querySelectorAll('source[data-src]');

  sources.forEach((source) => {
    source.src = source.dataset.src;

    if (source.dataset.type) {
      source.type = source.dataset.type;
    }

    if (source.dataset.media) {
      source.media = source.dataset.media;
    }
  });

  video.dataset.videoLoaded = 'true';
};

class VideoBackgroundText extends HTMLElement {
  constructor() {
    super();
    this.video = this.querySelector('.video-background-text__video');
    this.playPauseButton = this.querySelector('.video-background-text__play-pause-button');
    this.playIcon = this.querySelector('.video-background-text__play-icon');
    this.pauseIcon = this.querySelector('.video-background-text__pause-icon');
    this.userPaused = false;

    if (this.video && this.playPauseButton) {
      this.bindEvents();
      this.setupIntersectionObserver();
      this.updateButtonState();
    }
  }

  bindEvents() {
    this.playPauseButton.addEventListener('click', this.togglePlayPause.bind(this));
    this.video.addEventListener('play', this.updateButtonState.bind(this));
    this.video.addEventListener('pause', this.updateButtonState.bind(this));
  }

  togglePlayPause() {
    if (this.video.paused) {
      this.userPaused = false;
      loadVideoBackgroundTextSources(this.video);
      playVideoBackgroundTextVideo(this.video);
    } else {
      this.userPaused = true;
      this.video.pause();
    }
  }

  setupIntersectionObserver() {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          loadVideoBackgroundTextSources(this.video);
          this.video.load();

          if (!this.userPaused) {
            playVideoBackgroundTextVideo(this.video);
          }
        } else if (!this.video.paused) {
          this.video.pause();
        }
      });
    }, {
      threshold: 0.25,
      rootMargin: '300px 0px'
    });

    observer.observe(this);
  }

  updateButtonState() {
    if (this.video.paused) {
      // Show play icon
      this.playIcon.style.display = 'block';
      this.pauseIcon.style.display = 'none';
      this.playPauseButton.setAttribute('aria-label', 'Play video');
    } else {
      // Show pause icon
      this.playIcon.style.display = 'none';
      this.pauseIcon.style.display = 'block';
      this.playPauseButton.setAttribute('aria-label', 'Pause video');
    }
  }
}

if (!customElements.get('video-background-text')) {
  customElements.define('video-background-text', VideoBackgroundText);
}
